import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Filter } from "lucide-react";
import { useI18n, useLocalizedPath } from "../i18n/I18nContext";
import { api } from "../lib/api";
import Icon from "../components/Icon";
import { TID } from "../constants/testIds";

export default function Guides() {
  const { t, lang } = useI18n();
  const lp = useLocalizedPath();
  const [searchParams, setSearchParams] = useSearchParams();
  const category = searchParams.get("category");
  const [guides, setGuides] = useState([]);
  const [cats, setCats] = useState([]);
  const [active, setActive] = useState(category || "all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get("/guides", { params: active === "all" ? {} : { category: active } }),
      cats.length ? Promise.resolve({ data: cats }) : api.get("/categories"),
    ])
      .then(([g, c]) => {
        setGuides(g.data);
        setCats(c.data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
    // eslint-disable-next-line
  }, [active]);

  useEffect(() => {
    setActive(category || "all");
  }, [category]);

  const selectCategory = (slug) => {
    setActive(slug);
    if (slug === "all") setSearchParams({});
    else setSearchParams({ category: slug });
  };

  return (
    <div className="container mx-auto px-6 py-12 md:py-20">
      <div className="max-w-3xl mb-12">
        <p className="font-mono-label text-gold/80 mb-4">
          {lang === "en" ? "Library · Action guides" : "Bibliotecă · Ghiduri de acțiune"}
        </p>
        <h1 className="font-display text-4xl md:text-6xl tracking-tighter">
          {lang === "en" ? "Pick a chapter to rewrite." : "Alege un capitol de rescris."}
        </h1>
        <p className="mt-6 text-white/55 text-base md:text-lg">
          {lang === "en"
            ? "Every guide is short, surgical, and built around action. Choose your starting point."
            : "Fiecare ghid este scurt, precis și construit în jurul acțiunii. Alege punctul tău de plecare."}
        </p>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 mb-10 flex-wrap">
        <span className="font-mono-label text-white/40 flex items-center gap-2">
          <Filter className="h-3 w-3" />
          {lang === "en" ? "Filter" : "Filtrează"}
        </span>
        <FilterPill
          active={active === "all"}
          onClick={() => selectCategory("all")}
          label={lang === "en" ? "All" : "Toate"}
          testId="filter-all"
        />
        {cats.map((c) => (
          <FilterPill
            key={c.slug}
            active={active === c.slug}
            onClick={() => selectCategory(c.slug)}
            label={lang === "en" ? c.name_en : c.name_ro}
            testId={`filter-${c.slug}`}
          />
        ))}
      </div>

      {/* Grid */}
      <div
        data-testid={TID.guidesGrid}
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
      >
        {loading
          ? Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="glass rounded-2xl p-8 h-72 animate-pulse" />
            ))
          : guides.map((g, i) => {
              const tr = g.translations[lang];
              return (
                <motion.div
                  key={g.slug}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: (i % 9) * 0.04, ease: [0.16, 1, 0.3, 1] }}
                >
                  <Link
                    to={lp(`/guides/${g.slug}`)}
                    data-testid={TID.guideCard(g.slug)}
                    className="glass glass-hover rounded-2xl p-8 group block h-full relative"
                  >
                    <div className="flex items-start justify-between mb-8">
                      <div className="h-10 w-10 rounded-xl bg-gold/10 border border-gold/20 flex items-center justify-center">
                        <Icon name={g.icon} className="h-5 w-5 text-gold" strokeWidth={1.5} />
                      </div>
                      <span className="font-mono-label text-white/40">
                        {g.reading_time_min}m
                      </span>
                    </div>
                    <h3 className="font-display text-2xl tracking-tight leading-snug">{tr.title}</h3>
                    <p className="mt-3 text-sm text-white/55 line-clamp-3">{tr.subtitle}</p>
                    <div className="mt-8 flex items-center justify-between">
                      <span className="font-mono-label text-white/50">€{g.price.toFixed(0)}</span>
                      <span className="text-xs font-mono-label text-white/40 group-hover:text-gold transition-colors flex items-center gap-1">
                        {lang === "en" ? "Open" : "Deschide"}
                        <ArrowUpRight className="h-3.5 w-3.5" />
                      </span>
                    </div>
                  </Link>
                </motion.div>
              );
            })}
      </div>
    </div>
  );
}

function FilterPill({ active, onClick, label, testId }) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      className={`text-xs font-mono-label px-3 py-1.5 rounded-full border transition-all ${
        active
          ? "bg-gold text-obsidian-900 border-gold"
          : "border-white/10 text-white/60 hover:border-white/30 hover:text-white"
      }`}
    >
      {label}
    </button>
  );
}
